import { useMutation, useQuery } from "@tanstack/solid-query";
import { type Accessor, createMemo } from "solid-js";
import {
  axiosInstance,
  queryClient,
  setAuthToken,
  setRefreshToken,
} from "@/api/client";
import {
  ATS_KEY,
  AUTH_KEY,
  BILLING_USAGE_KEY,
  COMPANY_KEY,
  JOB_OFFER_KEY,
  PROFILE_KEY,
} from "@/api/query-keys";
import { isNotFoundError } from "@/api/utils/error";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import type { SessionBootstrapResponse } from "@/domains/auth/session/types";
import type { JobOfferV2WithCompanyId } from "@/domains/job-offer/types";
import { devLog } from "@/libs/dev-log";
import { applyCompanyPatchToCache } from "./cache";
import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  CompanyInvitation,
  CompanyInvitationStatus,
  CompanyMember,
  CompanyOnboardingRequestBody,
  CompanyOnboardingResponse,
  CompanyTransactions,
  CompanyV2,
  CompanyV2Update,
  ContactCandidateResponse,
  CreateCompanyV2Body,
  CreateInvitationRequest,
  DashboardActivityResponse,
  DashboardStatsResponse,
  GiveFeedbackRequestBody,
  GiveFeedbackResponse,
  JobApplicationStatus,
  JobApplicationsResponse,
  UpdateCompanyCover,
  UpdateCompanyImage,
} from "./types";
export const getUserCompanies = async (id: string) => {
  const { data } = await axiosInstance.get<{
    companies: CompanyV2[];
  }>(`/company/profile/${id}`);
  return data;
};

/**
 * The authenticated read: every company the profile can act in, each with the
 * caller's `role`/`is_owner` and the `invoice_*` fields the public DTO of
 * `GET /company/:id` does not carry. Its key is the bare `[COMPANY_KEY]`
 * prefix, so anything invalidating that prefix refetches this list.
 *
 * `enabled` guards the non-null assertions below: without a profile there is
 * no id to put in the URL, and firing the query anyway produced a request to
 * `/company/profile/undefined`.
 */
export const useGetUserCompaniesV2 = () => {
  const [auth] = useAuthV2();
  return useQuery(() => ({
    queryKey: [COMPANY_KEY],
    queryFn: () => getUserCompanies(auth!.profile!.id!),
    enabled: !!auth?.profile?.id,
  }));
};

export const getCompanyV2 = async (companyId: string) => {
  const { data } = await axiosInstance.get<{ company: CompanyV2 }>(
    `/company/${companyId}`,
  );
  return data;
};

/** TanStack's own default, spelled out because the retry below replaces it. */
const COMPANY_NETWORK_RETRIES = 3;

/**
 * The company read, shared by the surfaces that show a company they did not
 * fetch themselves: the offer detail sheet and the two creation previews.
 *
 * The only thing this policy states beyond the defaults is that **a 404 is not
 * asked again**. TanStack retries three times by default and its default
 * predicate does not look at the status, so a company that does not exist costs
 * four requests and about seven seconds — during which the caller can only show
 * a loading state, because the query is still `pending`. A missing company is an
 * answer, not an unreliable network: asking again returns the same 404.
 *
 * The count for everything else stays at the default here, while the public
 * page's config lowers it to 2 — but that is a difference on paper, not an
 * isolation. Both configs use the key `[COMPANY_KEY, companyId]`, and
 * `QueryObserver.setOptions` writes its own options onto the shared `Query`
 * (`query-core@5.95.2`, `queryObserver.js:94`), so the count actually in force
 * is whichever observer set options last. It costs nothing today because the
 * two differ only between 2 and 3 attempts on errors that are worth retrying;
 * what they agree on — the 404 exemption — is the part that would be visible if
 * it were lost, and neither config can lose it for the other.
 */
export const createCompanyQueryConfig = (companyId: string) => {
  return {
    queryKey: [COMPANY_KEY, companyId],
    queryFn: () => getCompanyV2(companyId),
    enabled: !!companyId,
    retry: (failureCount: number, error: Error) =>
      !isNotFoundError(error) && failureCount < COMPANY_NETWORK_RETRIES,
  };
};

/**
 * An accessor, not a string, like every other company read in this file
 * (`useGetCompanyMembers`, `useGetCompanyInvitations`, `useGetDashboardStats`).
 *
 * The string form captured the id at setup, which made every call site depend
 * on being unmounted to ever ask about a different company. That held by
 * accident where a Kobalte dialog wrapped the caller, and not at all where one
 * did not: the id changed, the query kept answering about the previous company,
 * and the surface rendered the wrong name under the right details — with full
 * confidence, which is worse than an error state.
 */
export const useGetCompanyV2 = (companyIdAccessor: Accessor<string>) => {
  return useQuery(() => createCompanyQueryConfig(companyIdAccessor()));
};

/**
 * The response carries the whole updated company, which is what lets the
 * settings page write its caches instead of invalidating them (D8).
 */
export const patchCompany = async ({
  companyId,
  body,
}: {
  companyId: string;
  body: CompanyV2Update;
}) => {
  const { data } = await axiosInstance.patch<{ company: CompanyV2 }>(
    `/company/${companyId}`,
    {
      company: body,
    },
  );
  return data;
};

/**
 * Query keys a company creation makes stale, whichever endpoint created it.
 *
 * The two creation mutations used to invalidate different sets — only v2 touched
 * `BILLING_USAGE`, only onboarding touched `AUTH` — so the very same event left
 * the app in two different states depending on which door the user came through.
 * Both now invalidate all four (SPEC AC 19).
 */
const COMPANY_CREATION_INVALIDATED_KEYS = [
  AUTH_KEY,
  COMPANY_KEY,
  PROFILE_KEY,
  BILLING_USAGE_KEY,
];

const invalidateAfterCompanyCreation = () => {
  for (const key of COMPANY_CREATION_INVALIDATED_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
};

/**
 * `body` is sent as-is: it is the output of `buildCreationPayload`
 * (`onboarding/utils/payload.ts`) and the four prefill fields inside it must
 * reach the BE byte-for-byte as the LinkedIn fetch returned them (contract
 * §1.3). The `{ company }` envelope is the wire format, not a reshaping.
 */
const createCompany = async (body: CreateCompanyV2Body) => {
  const { data } = await axiosInstance.post<{
    company: CompanyV2;
  }>("/company/v2", {
    company: body,
  });
  return data;
};

export const useCreateCompany = () => {
  return useMutation(() => ({
    mutationFn: (body: CreateCompanyV2Body) => createCompany(body),
    onSuccess: invalidateAfterCompanyCreation,
  }));
};

export type CreateSoloFreelancerWorkspacePayload = {
  name?: string;
};

export type WorkspaceLifecycleResponse = {
  workspace: CompanyV2;
  context: SessionBootstrapResponse;
};

export const createSoloFreelancerWorkspace = async (
  body: CreateSoloFreelancerWorkspacePayload = {},
) => {
  const { data } = await axiosInstance.post<WorkspaceLifecycleResponse>(
    "/company/solo-freelancer",
    body,
  );
  return data;
};

const archiveSoloFreelancerWorkspace = async (companyId: string) => {
  const { data } = await axiosInstance.post<WorkspaceLifecycleResponse>(
    `/company/${companyId}/archive-solo-freelancer`,
  );
  return data;
};

const reactivateSoloFreelancerWorkspace = async (companyId: string) => {
  const { data } = await axiosInstance.post<WorkspaceLifecycleResponse>(
    `/company/${companyId}/reactivate-solo-freelancer`,
  );
  return data;
};

export type ConvertSoloFreelancerToCompanyPayload = {
  invoice_email: string;
  invoice_address: string;
  invoice_vat: string;
  name?: string;
  description?: string | null;
  website?: string | null;
  industry?: string | null;
  company_size?: string | null;
  founded_year?: string | null;
  headquarters_location?: string | null;
};

const convertSoloFreelancerToCompany = async ({
  companyId,
  body,
}: {
  companyId: string;
  body: ConvertSoloFreelancerToCompanyPayload;
}) => {
  const { data } = await axiosInstance.post<WorkspaceLifecycleResponse>(
    `/company/${companyId}/convert-to-company`,
    body,
  );
  return data;
};

const invalidateWorkspaceLifecycleQueries = () => {
  queryClient.invalidateQueries({ queryKey: [AUTH_KEY] });
  queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
  queryClient.invalidateQueries({ queryKey: [ATS_KEY] });
  queryClient.invalidateQueries({ queryKey: [JOB_OFFER_KEY] });
  queryClient.invalidateQueries({ queryKey: [BILLING_USAGE_KEY] });
};

export const useCreateSoloFreelancerWorkspace = () => {
  return useMutation(() => ({
    mutationFn: createSoloFreelancerWorkspace,
    onSuccess: invalidateWorkspaceLifecycleQueries,
  }));
};

export const useArchiveSoloFreelancerWorkspace = () => {
  return useMutation(() => ({
    mutationFn: archiveSoloFreelancerWorkspace,
    onSuccess: invalidateWorkspaceLifecycleQueries,
  }));
};

export const useReactivateSoloFreelancerWorkspace = () => {
  return useMutation(() => ({
    mutationFn: reactivateSoloFreelancerWorkspace,
    onSuccess: invalidateWorkspaceLifecycleQueries,
  }));
};

export const useConvertSoloFreelancerToCompany = () => {
  return useMutation(() => ({
    mutationFn: convertSoloFreelancerToCompany,
    onSuccess: invalidateWorkspaceLifecycleQueries,
  }));
};

/**
 * Persist the user's active workspace choice on the backend.
 * The backend stores it as the profile's `last_selected_workspace_id` and
 * `/session/bootstrap` returns it as `selected_workspace` on the next session,
 * so the selection survives a reload / re-login instead of reverting to the
 * previous workspace. Returns a fresh lifecycle context (capabilities,
 * subscription and companies can differ per workspace).
 */
export const selectWorkspace = async (companyId: string) => {
  const { data } = await axiosInstance.post<WorkspaceLifecycleResponse>(
    `/company/${companyId}/select`,
  );
  return data;
};

export const useSelectWorkspaceMutation = () =>
  useMutation(() => ({
    mutationFn: (companyId: string) => selectWorkspace(companyId),
  }));

/**
 * Every offer of a company, as the backend chooses to hand them over.
 *
 * No `payment_status` any more: the frontend used to send `"completed"` and the
 * backend never read it — the parameter is not even in its handler signature.
 * A filter that looks applied and is not is worse than none, and it made the
 * query key lie: the key never carried the parameter, so two callers asking for
 * different statuses would have shared one cache entry.
 *
 * The real visibility filter lives on the caller (AC 9) and, once its PR lands,
 * on the backend (AC 21).
 */
const getAllCompanyJobOffers = async (companyId: string) => {
  const { data } = await axiosInstance.get<{
    job_offers: JobOfferV2WithCompanyId[];
  }>(`/company/job-offers/${companyId}`);
  return data;
};

const getAllCompaniesJobOffersCount = async ({
  profileId,
}: {
  profileId: string;
}) => {
  const data = await getUserCompanies(profileId);
  const companiesIds = data.companies.map((company) => company.id);
  // Start all job offers requests in parallel
  const jobOffersCount = await Promise.all(
    companiesIds.map((companyId) => getAllCompanyJobOffers(companyId)),
  );
  const companyJobOffersCount = data.companies.reduce(
    (acc, company, index) => {
      acc[company.id] = jobOffersCount[index].job_offers.length;
      return acc;
    },
    {} as Record<string, number>,
  );
  return {
    companies: data.companies,
    count: companyJobOffersCount,
  };
};

export const useGetAllCompaniesJobOffersCount = () => {
  const [auth] = useAuthV2();
  return useQuery(() => ({
    queryKey: [COMPANY_KEY, "job-offers-count", auth!.profile!.id!],
    queryFn: () =>
      getAllCompaniesJobOffersCount({ profileId: auth!.profile!.id! }),
    enabled: !!auth!.profile!.id,
  }));
};

export const useGetAllCompanyJobOffersV2 = (companyId: string) => {
  return useQuery(() => ({
    queryKey: [COMPANY_KEY, JOB_OFFER_KEY, companyId],
    queryFn: () => getAllCompanyJobOffers(companyId),
    enabled: !!companyId,
  }));
};

const updateCompanyImage = async (body: UpdateCompanyImage) => {
  const formData = new FormData();
  formData.append("image", body.image);
  const { data } = await axiosInstance.post(
    `/company/logo/${body.id}`,
    formData,
  );
  return data;
};

export const useUpdateCompanyImage = () => {
  return useMutation(() => ({
    mutationFn: (body: UpdateCompanyImage) => updateCompanyImage(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [JOB_OFFER_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [ATS_KEY],
      });
    },
  }));
};

/** Both cover routes answer with the same shape, upload and removal alike. */
type CoverResponse = {
  msg: string;
  company: Pick<CompanyV2, "id" | "name" | "cover_image_url">;
};

const updateCompanyCover = async (body: UpdateCompanyCover) => {
  const formData = new FormData();
  // `cover`, not `image`: the route's multer is `single("cover")`.
  formData.append("cover", body.cover);
  const { data } = await axiosInstance.post<CoverResponse>(
    `/company/cover/${body.id}`,
    formData,
  );
  return data;
};

/**
 * Only the company queries are invalidated, unlike the logo above: the cover
 * belongs to the company page alone, while the logo rides along on every offer
 * and ATS card.
 */
export const useUpdateCompanyCover = () => {
  return useMutation(() => ({
    mutationFn: (body: UpdateCompanyCover) => updateCompanyCover(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY],
      });
    },
  }));
};

/**
 * Removing the cover has existed on the backend since the LinkedIn import — and
 * is idempotent there, a company with no cover still answers 200 — but had no
 * caller here, so the settings page could offer replacing a cover and not
 * removing one (EP11).
 *
 * Unlike the two upload mutations it writes the caches instead of invalidating
 * them: it is new, so nothing else depends on the refetch, and the settings
 * page must not have fields repainted from the server under the cursor (D8).
 */
const deleteCompanyCover = async (companyId: string) => {
  const { data } = await axiosInstance.delete<CoverResponse>(
    `/company/cover/${companyId}`,
  );
  return data;
};

export const useDeleteCompanyCover = () => {
  return useMutation(() => ({
    mutationFn: (companyId: string) => deleteCompanyCover(companyId),
    onSuccess: (data, companyId) => {
      applyCompanyPatchToCache(
        queryClient,
        companyId,
        data.company as CompanyV2,
        ["cover_image_url"],
      );
    },
  }));
};

export const getCompanyTransactions = async (companyId: string) => {
  const { data } = await axiosInstance.get<CompanyTransactions>(
    `/company/payments/${companyId}`,
  );
  return data;
};

export const useGetCompanyTransactions = (companyId: string) => {
  return useQuery(() => ({
    queryKey: [COMPANY_KEY, "payments", companyId],
    queryFn: () => getCompanyTransactions(companyId),
  }));
};

type CheckCompanyVatResponse = {
  vat_uniqueness: boolean;
};

export const checkCompanyVat = async (
  VAT: string,
): Promise<CheckCompanyVatResponse> => {
  const { data } = await axiosInstance.get<CheckCompanyVatResponse>(
    `/company/vat-uniqueness/${VAT}`,
  );
  return data;
};

export const useCheckCompanyVat = () => {
  return useMutation(() => ({
    mutationFn: (VAT: string) => checkCompanyVat(VAT),
  }));
};

export const getJobApplicationsForJobOffer = async (
  jobOfferId: string,
  filters?: JobApplicationsFilters,
) => {
  const params = new URLSearchParams();

  if (filters) {
    devLog("Filtri ricevuti:", filters);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      } else {
        devLog(`Parametro ${key} ignorato (vuoto/null/undefined)`);
      }
    });
  } else {
    devLog("Nessun filtro ricevuto");
  }

  const url = `/company/job-offers/applications/${jobOfferId}${params.toString() ? `?${params.toString()}` : ""}`;

  const { data } = await axiosInstance.get<JobApplicationsResponse>(url);
  return data;
};

export type JobApplicationsFilters = {
  status?: string;
  has_been_viewed?: string;
  created_after?: string;
  created_before?: string;
  viewed_after?: string;
  viewed_before?: string;
  contacted_after?: string;
  contacted_before?: string;
  sort_by?: string;
  sort_order?: string;
  initiator?: "freelancer" | "company";
};

export const useGetJobApplicationsForJobOffer = (
  jobOfferId: string,
  filters?: JobApplicationsFilters | (() => JobApplicationsFilters),
  options?: { enabled?: boolean },
) => {
  // Create a reactive query that updates when filters change
  const query = createMemo(() => {
    // Ensure filters is reactive by accessing its properties
    const currentFilters = typeof filters === "function" ? filters() : filters;

    // Create a stable query key that changes when filters change
    // Use JSON.stringify to ensure the entire filters object is tracked
    const filtersString = JSON.stringify(currentFilters || {});

    const queryKey = [
      COMPANY_KEY,
      "job-applications",
      jobOfferId,
      filtersString,
    ];

    return {
      queryKey,
      queryFn: () => getJobApplicationsForJobOffer(jobOfferId, currentFilters),
      enabled: !!jobOfferId && options?.enabled !== false,
    };
  });

  return useQuery(query);
};

type ContactCandidateParams = {
  jobOfferId: string;
  jobApplicationId: string;
  customMessage?: string | null;
  calendarUrl?: string | null;
};

const contactCandidate = async ({
  jobOfferId,
  jobApplicationId,
  customMessage,
  calendarUrl,
}: ContactCandidateParams): Promise<ContactCandidateResponse> => {
  const { data } = await axiosInstance.post<ContactCandidateResponse>(
    `/company/job-offers/${jobOfferId}/applications/${jobApplicationId}/contact`,
    customMessage || calendarUrl
      ? {
          ...(customMessage ? { custom_message: customMessage } : {}),
          ...(calendarUrl ? { calendar_url: calendarUrl } : {}),
        }
      : {},
  );
  return data;
};

export const useContactCandidate = () => {
  return useMutation(() => ({
    mutationFn: ({
      jobOfferId,
      jobApplicationId,
      customMessage,
      calendarUrl,
    }: ContactCandidateParams) =>
      contactCandidate({
        jobOfferId,
        jobApplicationId,
        customMessage,
        calendarUrl,
      }),
    onSuccess: () => {
      // Invalidate all job applications queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, "job-applications"],
      });
    },
  }));
};

type GiveFeedbackParams = {
  jobOfferId: string;
  jobApplicationId: string;
  feedback: JobApplicationStatus;
} & Pick<GiveFeedbackRequestBody, "rejectionReasons" | "rejectionMessage">;

const giveFeedbackToCandidate = async ({
  jobOfferId,
  jobApplicationId,
  feedback,
  rejectionReasons,
  rejectionMessage,
}: GiveFeedbackParams): Promise<GiveFeedbackResponse> => {
  const payload: GiveFeedbackRequestBody = {
    status: feedback,
  };

  if (rejectionReasons && rejectionReasons.length > 0) {
    payload.rejectionReasons = rejectionReasons;
  }

  const trimmedMessage = rejectionMessage?.trim();
  if (trimmedMessage) {
    payload.rejectionMessage = trimmedMessage;
  }

  const { data } = await axiosInstance.post<GiveFeedbackResponse>(
    `/company/job-offers/${jobOfferId}/applications/${jobApplicationId}/feedback`,
    payload,
  );
  return data;
};

export const useGiveFeedbackToCandidate = () => {
  return useMutation(() => ({
    mutationFn: ({
      jobOfferId,
      jobApplicationId,
      feedback,
      rejectionReasons,
      rejectionMessage,
    }: GiveFeedbackParams) =>
      giveFeedbackToCandidate({
        jobOfferId,
        jobApplicationId,
        feedback,
        rejectionReasons,
        rejectionMessage,
      }),
    onSuccess: () => {
      // Invalidate all job applications queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, "job-applications"],
      });
    },
  }));
};

type UpdateApplicationNoteParams = {
  jobOfferId: string;
  jobApplicationId: string;
  note: string | null;
};

const updateApplicationNote = async ({
  jobOfferId,
  jobApplicationId,
  note,
}: UpdateApplicationNoteParams): Promise<{ msg: string }> => {
  const { data } = await axiosInstance.patch<{ msg: string }>(
    `/company/job-offers/${jobOfferId}/applications/${jobApplicationId}`,
    {
      job_application: {
        note,
      },
    },
  );
  return data;
};

export const useUpdateApplicationNote = () => {
  return useMutation(() => ({
    mutationFn: ({
      jobOfferId,
      jobApplicationId,
      note,
    }: UpdateApplicationNoteParams) =>
      updateApplicationNote({
        jobOfferId,
        jobApplicationId,
        note,
      }),
    onSuccess: () => {
      // Invalidate all job applications queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, "job-applications"],
      });
    },
  }));
};

/**
 * Twin of `createCompany` on the onboarding endpoint. Same rule: `body.company`
 * is `buildCreationPayload`'s output, unreshaped.
 */
const companyOnboarding = async (
  body: CompanyOnboardingRequestBody,
): Promise<CompanyOnboardingResponse> => {
  const { data } = await axiosInstance.post<CompanyOnboardingResponse>(
    "/company/onboarding",
    body,
  );
  return data;
};

export const useCompanyOnboarding = () => {
  return useMutation(() => ({
    mutationFn: (body: CompanyOnboardingRequestBody) => companyOnboarding(body),
    onSuccess: invalidateAfterCompanyCreation,
  }));
};

// Company Invitations API
const createInvitation = async (
  companyId: string,
  body: CreateInvitationRequest,
) => {
  const { data } = await axiosInstance.post<{ invitation: CompanyInvitation }>(
    `/company/${companyId}/invitations`,
    body,
  );
  return data;
};

export const useCreateInvitation = () => {
  return useMutation(() => ({
    mutationFn: ({
      companyId,
      body,
    }: {
      companyId: string;
      body: CreateInvitationRequest;
    }) => createInvitation(companyId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, "invitations"],
      });
      queryClient.invalidateQueries({ queryKey: [BILLING_USAGE_KEY] });
    },
  }));
};

const getCompanyInvitations = async (
  companyId: string,
  status?: CompanyInvitationStatus,
) => {
  const params = status ? { status } : {};
  const { data } = await axiosInstance.get<{
    invitations: CompanyInvitation[];
  }>(`/company/${companyId}/invitations`, { params });
  return data;
};

export const useGetCompanyInvitations = (
  companyIdAccessor: Accessor<string>,
  status?: CompanyInvitationStatus,
  options?: { enabled?: Accessor<boolean> },
) => {
  return useQuery(() => {
    const companyId = companyIdAccessor();
    const enabled = options?.enabled?.() ?? true;
    return {
      queryKey: [COMPANY_KEY, "invitations", companyId, status],
      queryFn: () => getCompanyInvitations(companyId, status),
      enabled: !!companyId && enabled,
    };
  });
};

const cancelInvitation = async (invitationId: string) => {
  const { data } = await axiosInstance.delete<{ success: boolean }>(
    `/company/invitations/${invitationId}`,
  );
  return data;
};

export const useCancelInvitation = () => {
  return useMutation(() => ({
    mutationFn: (invitationId: string) => cancelInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, "invitations"],
      });
    },
  }));
};

const acceptInvitation = async (body: AcceptInvitationRequest) => {
  const { data } = await axiosInstance.post<AcceptInvitationResponse>(
    "/company/invitations/accept",
    body,
  );
  return data;
};

export const useAcceptInvitation = () => {
  return useMutation(() => ({
    mutationFn: (body: AcceptInvitationRequest) => acceptInvitation(body),
    onSuccess: async (data) => {
      // Salva i token se presenti (sempre generati dal backend)
      if (data.tokens) {
        await setAuthToken(data.tokens.accessToken);
        await setRefreshToken(data.tokens.refreshToken);
        // Invalida tutte le query per ricaricare profilo, company, ecc.
        queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] });
        queryClient.invalidateQueries({ queryKey: [AUTH_KEY] });
        queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
        queryClient.invalidateQueries({ queryKey: [BILLING_USAGE_KEY] });
      }
    },
  }));
};

// Company Members API
const getCompanyMembers = async (companyId: string) => {
  const { data } = await axiosInstance.get<{ members: CompanyMember[] }>(
    `/company/${companyId}/members`,
  );
  return data;
};

export const useGetCompanyMembers = (companyIdAccessor: Accessor<string>) => {
  return useQuery(() => {
    const companyId = companyIdAccessor();
    return {
      queryKey: [COMPANY_KEY, "members", companyId],
      queryFn: () => getCompanyMembers(companyId),
      enabled: !!companyId,
    };
  });
};

const removeMember = async (companyId: string, memberId: string) => {
  const { data } = await axiosInstance.delete<{ success: boolean }>(
    `/company/${companyId}/members/${memberId}`,
  );
  return data;
};

export const useRemoveMember = () => {
  return useMutation(() => ({
    mutationFn: ({
      companyId,
      memberId,
    }: {
      companyId: string;
      memberId: string;
    }) => removeMember(companyId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_KEY, "members"],
      });
      queryClient.invalidateQueries({ queryKey: [BILLING_USAGE_KEY] });
    },
  }));
};

// Dashboard API

const getDashboardStats = async (companyId: string) => {
  const { data } = await axiosInstance.get<DashboardStatsResponse>(
    `/v1/company/${companyId}/dashboard/stats`,
  );
  return data;
};

export const createDashboardStatsQueryConfig = (companyId: string) => ({
  queryKey: [COMPANY_KEY, "dashboard-stats", companyId],
  queryFn: () => getDashboardStats(companyId),
  enabled: !!companyId,
});

export const useGetDashboardStats = (companyIdAccessor: Accessor<string>) =>
  useQuery(() => {
    const companyId = companyIdAccessor();
    return createDashboardStatsQueryConfig(companyId);
  });

const getDashboardActivity = async (companyId: string, limit?: number) => {
  const { data } = await axiosInstance.get<DashboardActivityResponse>(
    `/v1/company/${companyId}/dashboard/recent-activity`,
    { params: limit !== undefined ? { limit } : {} },
  );
  return data;
};

export const createDashboardActivityQueryConfig = (
  companyId: string,
  limit?: number,
) => ({
  queryKey: [COMPANY_KEY, "dashboard-activity", companyId, limit ?? null],
  queryFn: () => getDashboardActivity(companyId, limit),
  enabled: !!companyId,
});

export const useGetDashboardActivity = (
  companyIdAccessor: Accessor<string>,
  limit?: number,
) =>
  useQuery(() => {
    const companyId = companyIdAccessor();
    return createDashboardActivityQueryConfig(companyId, limit);
  });
